/**
 * #363 — the site map: a camera group's own drawing of its site (DLR's depot track schematic) as a second view of the
 * dashboard's map window, with the group's cameras on it at hand-placed positions. The drawing is not to scale and has
 * no north, so nothing here is geographic: a pin is a fraction (0..1) of the drawing's width and height.
 *
 * The cameras are the drawing's own list (`cameras` in the site-plan GET, CP amendment 2026-10-18): every camera of the
 * group, with coordinates or not. Each takes its live state (online / offline, last event, alert, thumbnail) from the
 * map pins by camera id when it is there; a camera without coordinates is not among the map pins and is drawn, listed
 * and placed with a neutral state (status unknown, no picture). An API before the amendment sends no list: the group's
 * map pins (`group_ids`) are the list then.
 *
 * What it shares with the Google map (MapView, untouched): the pin (CameraPinSvg, with the company's map settings), the
 * hover card (PinHoverCard, with the hover standard of #289), the online / offline / alert / selected states (the
 * alerting cameras come from PersistentDashboardMap's own #306 window), and a pin click, which calls the same handler
 * the Google map's pin click calls. What it does not draw: direction wedges (the drawing has no compass), clusters.
 *
 * The drawing is fitted to its box by its width and height; the wheel, the zoom buttons and two fingers zoom it up to
 * 3x (the 1600 px drawing is the sharpest there is); a mouse drag or two fingers pan it, inside bounds
 * (lib/sitePlanView). On a touch screen one finger scrolls the page, as on the Google map (#302 'auto'). The pins are
 * counter-scaled, so they keep their size while the drawing grows under them. The toolbar sits above the drawing's
 * box, so it never covers a pin near the top edge.
 *
 * Arrange mode (only when the API says `can_arrange`): a panel lists the group's cameras by site number, placed or not;
 * pick one, then click its spot on the drawing; drag a pin to move it (it keeps where it was grabbed, and a press that
 * moves less than DRAG_SLOP_PX moves nothing); Save sends the whole set in one PUT, Cancel restores the saved
 * positions, Escape clears the pick. Positions are held inside 0.01..0.99 and snap nowhere. The panel sits beside the
 * drawing, below it under 760 px. The parent is told while arranging, so it can hold its view switch still.
 */
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, Move, ZoomIn, ZoomOut } from 'lucide-react'
import { sitePlansApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { useMapSettings } from '@/hooks/useMapSettings'
import { sitePlanError, useSitePlan } from '@/hooks/useSitePlan'
import { Button } from '@/components/ui/button'
import { CameraPinSvg, alertGeometry } from '@/components/map/CameraPinSvg'
import { PinHoverCard } from '@/components/map/PinHoverCard'
import type { MapMarker } from '@/components/map/MapView'
import { cameraNumber } from '@/lib/cameraNumber'
import { useHoverIntent } from '@/lib/hoverIntent'
import { PIN_COLOUR_HEX, PIN_SIZE_WIDTH } from '@/lib/mapSettings'
import {
  FITTED, SITE_MAX_ZOOM, asPercent, bySiteNumber, clampFraction, clampView, fitDrawing, panView, pointToFraction, zoomAround,
  type Point, type SiteView, type Size,
} from '@/lib/sitePlanView'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { CameraMapPin, MapSettings, SitePlanPin } from '@/types'

export interface SiteMapViewProps {
  groupId: number
  /** Every map pin (GET /cameras/map-pins/): the live state of the group's cameras, by camera id. */
  pins: CameraMapPin[]
  /** The page's filter (#247), as the Google map follows it; null = every camera. Arranging shows them all. */
  visibleIds?: number[] | null
  /** #306 — the cameras whose pins alert now, and when each one's newest alert arrived (the bounce). */
  pulses?: Record<number, true>
  alertStarts?: Record<number, number>
  /** #282 — the camera whose detail dialog is open. */
  selectedId?: number | null
  /** The Google map's pin-click handler. */
  onMarkerClick?: (id: number) => void
  /** #336 — a hover card's thumbnail failed: the pins' owner refreshes them once. */
  onThumbnailError?: () => void
  /** Told when arrange mode starts and ends (and false when the view goes away): unsaved moves are at stake. */
  onArrangingChange?: (arranging: boolean) => void
  className?: string
}

/** Wheel zoom per pixel of scroll (exp: 100 px of wheel = x1.22). */
const WHEEL_ZOOM_RATE = 0.002
/** One press of a zoom button. */
const ZOOM_STEP = 1.5
/** A press that moves less than this is a click, not a pan or a pin drag (review I1: a jittery tap moves nothing). */
const DRAG_SLOP_PX = 4

type Positions = Map<number, Point>
/** A camera of the group, with its map pin (live state) when it has one. */
interface SiteCamera { id: number; name: string; pin: CameraMapPin | null }
/** Where the box was on the page when a press started (review N1: measured once per press, not on every move). */
interface BoxOrigin { left: number; top: number }

const NO_SIZE: Size = { width: 0, height: 0 }
const at = (clientX: number, clientY: number, box: BoxOrigin): Point => ({ x: clientX - box.left, y: clientY - box.top })

function toMarker(camera: SiteCamera, pulse: boolean): MapMarker {
  const p = camera.pin
  // No map pin (no coordinates): a neutral camera — status unknown, no picture, no last event, no alert, no Plus Code.
  if (!p) return { id: camera.id, lat: NaN, lng: NaN, label: camera.name, pulse: false, isOnline: null, thumbnailUrl: null, lastEventType: null, lastEventAt: null }
  return {
    id: camera.id,
    lat: parseFloat(p.latitude),
    lng: parseFloat(p.longitude),
    label: camera.name,
    sublabel: p.location ?? undefined,
    address: p.location_address ?? null,
    thumbnailUrl: p.last_event_thumbnail_url ?? null,
    pulse,
    groupNames: p.group_names,
    isOnline: p.is_online,
    lastEventType: p.last_event_type,
    lastEventAt: p.last_event_at,
  }
}

export function SiteMapView({
  groupId, pins, visibleIds = null, pulses, alertStarts, selectedId = null, onMarkerClick, onThumbnailError, onArrangingChange, className,
}: SiteMapViewProps) {
  const { t } = useTranslation('cameras')
  const qc = useQueryClient()
  const settings = useMapSettings()
  const planQuery = useSitePlan(groupId)
  const plan = planQuery.data ?? null

  // ── The drawing's box and the fitted drawing ────────────────────────────────
  const viewportRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<Size>(NO_SIZE)
  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setBox((prev) => (prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height }))
    }
    measure()
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
    observer?.observe(el)
    return () => observer?.disconnect()
  }, [])
  const planWidth = plan?.width ?? 0
  const planHeight = plan?.height ?? 0
  const fit = useMemo(() => fitDrawing(box, { width: planWidth, height: planHeight }), [box, planWidth, planHeight])
  // The user's zoom and pan, held in bounds for the box as it is now (a resize or the arrange panel changes it).
  const [rawView, setView] = useState<SiteView>(FITTED)
  const view = clampView(rawView, box, fit)
  // The pointer and wheel handlers live outside React's render: they read the latest values here.
  const live = useRef({ view, box, fit })
  live.current = { view, box, fit }
  const boxOrigin = useCallback((): BoxOrigin => {
    const r = viewportRef.current?.getBoundingClientRect()
    return { left: r?.left ?? 0, top: r?.top ?? 0 }
  }, [])

  // ── The group's cameras and their positions ─────────────────────────────────
  const listed = plan?.cameras
  const pinsById = useMemo(() => new Map(pins.map((p) => [p.id, p])), [pins])
  const cameras = useMemo<SiteCamera[]>(() => bySiteNumber(listed
    ? listed.map((c) => ({ id: c.id, name: c.name, pin: pinsById.get(c.id) ?? null }))
    : pins.filter((p) => p.group_ids?.includes(groupId)).map((p) => ({ id: p.id, name: p.name, pin: p }))),
  [listed, pins, pinsById, groupId])
  const cameraIds = useMemo(() => new Set(cameras.map((c) => c.id)), [cameras])
  const saved = useMemo<Positions>(() => new Map((plan?.pins ?? []).map((p) => [p.camera_id, { x: p.x, y: p.y }])), [plan])
  // Arranging = a draft of the positions; null = showing the saved ones. Copied once, at "Arrange pins": a refresh of
  // the pins or the drawing meanwhile leaves it alone.
  const [draft, setDraft] = useState<Positions | null>(null)
  const arranging = draft !== null && !!plan?.can_arrange
  const positions = arranging ? draft : saved
  const [picked, setPicked] = useState<number | null>(null)
  const arrangingRef = useRef(arranging)
  arrangingRef.current = arranging
  const positionsRef = useRef(positions)
  positionsRef.current = positions

  const onArrangingChangeRef = useRef(onArrangingChange)
  onArrangingChangeRef.current = onArrangingChange
  useEffect(() => { onArrangingChangeRef.current?.(arranging) }, [arranging])
  useEffect(() => () => onArrangingChangeRef.current?.(false), [])

  const markers = useMemo(
    () => new Map(cameras.map((c) => [c.id, toMarker(c, Boolean(pulses?.[c.id]))])),
    [cameras, pulses],
  )
  const visible = useMemo(() => (visibleIds ? new Set(visibleIds) : null), [visibleIds])
  const drawn = cameras.filter((c) => positions.has(c.id) && (arranging || !visible || visible.has(c.id)))
  const notPlaced = cameras.filter((c) => !saved.has(c.id)).length
  const placed = cameras.filter((c) => positions.has(c.id)).length

  // ── Zoom ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    // Not React's onWheel: it is passive, and the page must not scroll while the drawing zooms (#302's greedy wheel).
    const onWheel = (e: WheelEvent) => {
      const { view: v, box: b, fit: f } = live.current
      if (!(f.width > 0)) return
      e.preventDefault()
      const lines = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? b.height : 1
      setView(zoomAround(v, Math.exp(-e.deltaY * lines * WHEEL_ZOOM_RATE), at(e.clientX, e.clientY, boxOrigin()), b, f))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [boxOrigin])
  const zoomBy = (factor: number) => setView(zoomAround(view, factor, { x: box.width / 2, y: box.height / 2 }, box, fit))

  // ── Pan, pinch and pin drag ─────────────────────────────────────────────────
  type Gesture = {
    origin: BoxOrigin; pointers: Map<number, Point>; touch: boolean; start: SiteView; from: Point; moved: boolean
    pinch?: { dist: number; mid: Point; start: SiteView }
  }
  type Drag = { id: number; pointerId: number; origin: BoxOrigin; press: Point; from: Point; moved: boolean }
  const gesture = useRef<Gesture | null>(null)
  const drag = useRef<Drag | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)
  // A press that panned the drawing or dragged a pin ends in a click: that click places nothing (review I1).
  const swallowClick = useRef(false)
  useEffect(() => {
    function onUp(e: PointerEvent) {
      const d = drag.current
      if (d && e.pointerId === d.pointerId) {
        if (d.moved) swallowClick.current = true
        drag.current = null
        setDragging(null)
        return
      }
      const g = gesture.current
      if (!g || !g.pointers.has(e.pointerId)) return
      if (g.moved) swallowClick.current = true
      g.pointers.delete(e.pointerId)
      const rest = [...g.pointers.values()][0]
      // the pinch's second finger lifted: the one left goes on from where it is
      gesture.current = rest ? { ...g, pinch: undefined, start: live.current.view, from: rest, moved: true } : null
    }
    const onMove = (e: PointerEvent) => {
      const d = drag.current
      const g = gesture.current
      if (!d && !g) return   // nothing pressed: measure nothing (review N1)
      // a mouse button released outside the window sends no pointerup here: the next move without it ends the press
      if (e.pointerType === 'mouse' && (e.buttons & 1) === 0) { onUp(e); return }
      if (d && e.pointerId === d.pointerId) {
        const point = at(e.clientX, e.clientY, d.origin)
        const dx = point.x - d.press.x
        const dy = point.y - d.press.y
        if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP_PX) return
        d.moved = true
        const { view: v, fit: f } = live.current
        const width = f.width * v.zoom
        const height = f.height * v.zoom
        if (!(width > 0 && height > 0)) return
        // the pin moves with the pointer from where it was grabbed: no jump to the tip (review I1)
        const next = { x: clampFraction(d.from.x + dx / width), y: clampFraction(d.from.y + dy / height) }
        setDraft((prev) => (prev ? new Map(prev).set(d.id, next) : prev))
        return
      }
      if (!g || !g.pointers.has(e.pointerId)) return
      g.pointers.set(e.pointerId, at(e.clientX, e.clientY, g.origin))
      const { box: b, fit: f } = live.current
      if (g.pointers.size >= 2) {
        const [p1, p2] = [...g.pointers.values()]
        const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y)
        const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
        if (!g.pinch || !(g.pinch.dist > 0)) { g.pinch = { dist, mid, start: live.current.view }; g.moved = true; return }
        const zoomed = zoomAround(g.pinch.start, dist / g.pinch.dist, g.pinch.mid, b, f)
        setView(panView(zoomed, mid.x - g.pinch.mid.x, mid.y - g.pinch.mid.y, b, f))
        return
      }
      // one finger on a touch screen is the page's (it scrolls, #302 'auto'); a mouse or a pen pans the drawing
      if (g.touch) return
      const point = g.pointers.get(e.pointerId)!
      const dx = point.x - g.from.x
      const dy = point.y - g.from.y
      if (!g.moved && Math.hypot(dx, dy) < DRAG_SLOP_PX) return
      g.moved = true
      setView(panView(g.start, dx, dy, b, f))
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [])

  const onViewportPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if ((e.target as Element).closest('[data-site-pin]')) return
    const origin = boxOrigin()
    const point = at(e.clientX, e.clientY, origin)
    const touch = e.pointerType === 'touch'
    // a mouse press always starts its own gesture; a second finger joins the first one's
    const g = touch && gesture.current?.touch ? gesture.current : null
    if (!g) {
      swallowClick.current = false
      gesture.current = { origin, pointers: new Map([[e.pointerId, point]]), touch, start: view, from: point, moved: false }
      return
    }
    g.pointers.set(e.pointerId, point)
    g.pinch = undefined   // set on the next move, from both fingers
  }

  const startDrag = useCallback((id: number, e: ReactPointerEvent<HTMLElement>) => {
    if (!arrangingRef.current) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const from = positionsRef.current.get(id)
    if (!from) return
    e.preventDefault()
    const origin = boxOrigin()
    drag.current = { id, pointerId: e.pointerId, origin, press: at(e.clientX, e.clientY, origin), from, moved: false }
    // the pin keeps the pointer while it is dragged (arrange mode only; jsdom has no pointer capture)
    e.currentTarget.setPointerCapture?.(e.pointerId)
    setDragging(id)
  }, [boxOrigin])

  // ── Arrange mode ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!arranging) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPicked(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [arranging])

  const onViewportClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (swallowClick.current) { swallowClick.current = false; return }
    if (!arranging || picked === null) return
    if ((e.target as Element).closest('[data-site-pin]')) return
    const f = pointToFraction(at(e.clientX, e.clientY, boxOrigin()), view, fit)
    if (!(f.x >= 0 && f.x <= 1 && f.y >= 0 && f.y <= 1)) return   // beside the drawing
    const id = picked
    setDraft((prev) => (prev ? new Map(prev).set(id, { x: clampFraction(f.x), y: clampFraction(f.y) }) : prev))
    setPicked(null)
  }

  const startArranging = () => { setDraft(new Map(saved)); setPicked(null) }
  const stopArranging = () => { setDraft(null); setPicked(null); drag.current = null; setDragging(null) }

  // The whole set, as the API replaces it: the group's cameras from the draft (the drawing's own list). An API before
  // the amendment has no list: a pin of a camera known to be outside the group is dropped (the API would refuse the
  // set) and one of a camera missing from the map pins (no coordinates) is kept as saved.
  const payload = (): SitePlanPin[] => {
    const out = new Map<number, Point>()
    for (const [id, pos] of draft ?? []) {
      if (cameraIds.has(id) || (!listed && !pinsById.has(id))) out.set(id, pos)
    }
    return [...out].sort(([a], [b]) => a - b).map(([camera_id, pos]) => ({ camera_id, x: pos.x, y: pos.y }))
  }
  const save = useMutation({
    mutationFn: (next: SitePlanPin[]) => sitePlansApi.savePins(groupId, next),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.sitePlans.group(groupId), fresh)
      toast.success(t('map.site.saved', { placed, total: cameras.length }))
      stopArranging()
    },
    onError: (error) => {
      const reason = sitePlanError(error)
      if (reason) toast.error(t('map.site.saveFailed'), reason)
      else toast.error(t('map.site.saveFailed'))
    },
  })

  // ── The drawing's picture: an expired link refreshes the drawing once ───────
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const retriedFor = useRef<string | null>(null)
  const planVersion = plan ? `${plan.id}:${plan.updated_at}` : null
  const onImageError = () => {
    if (!plan) return
    setFailedSrc(plan.image_url)
    if (retriedFor.current === planVersion) return
    retriedFor.current = planVersion
    void planQuery.refetch()
  }
  const imageFailed = !!plan && failedSrc === plan.image_url

  // ── Pin callbacks (stable: the pins are memoised) ───────────────────────────
  const onMarkerClickRef = useRef(onMarkerClick)
  onMarkerClickRef.current = onMarkerClick
  const activate = useCallback((id: number) => { if (!arrangingRef.current) onMarkerClickRef.current?.(id) }, [])
  const pickedCamera = picked !== null ? cameras.find((c) => c.id === picked) : undefined

  let message: ReactNode = null
  if (planQuery.isPending) message = <><Loader2 className="h-4 w-4 animate-spin" />{t('map.site.loading')}</>
  else if (planQuery.isError) {
    message = (
      <>
        <span>{t('map.site.loadFailed')}</span>
        <Button size="sm" variant="outline" onClick={() => { void planQuery.refetch() }}>{t('map.site.retry')}</Button>
      </>
    )
  } else if (!plan) message = t('map.site.noPlan')
  else if (imageFailed) message = planQuery.isFetching ? <><Loader2 className="h-4 w-4 animate-spin" />{t('map.site.loading')}</> : t('map.site.imageFailed')

  // The zoom buttons sit in a bordered pair with no overflow clip, so the focus ring shows (review M2).
  const zoomButton = 'relative flex h-7 w-7 items-center justify-center text-foreground hover:bg-muted focus-visible:z-10 disabled:opacity-40'

  return (
    <div data-site-map className={cn('flex h-full w-full overflow-hidden', arranging && 'max-[760px]:flex-col', className)}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* The toolbar above the drawing's box, not over it: a pin near the top edge stays reachable (review N6) */}
        {plan && !imageFailed && (
          <div data-site-toolbar className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border bg-background px-2 py-1">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {!arranging && notPlaced > 0 && (
                <span className="inline-flex h-7 items-center rounded-full border border-border bg-background px-2.5 text-xs font-medium text-muted-foreground">
                  {t('map.site.notPlaced', { count: notPlaced })}
                </span>
              )}
              {arranging && (
                <span className="inline-flex min-h-7 items-center py-1 text-xs text-foreground" aria-live="polite">
                  {pickedCamera ? t('map.site.pickHint', { name: pickedCamera.name }) : t('map.site.dragHint')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {plan.can_arrange && !arranging && (
                <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={startArranging}>
                  <Move className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {t('map.site.arrange')}
                </Button>
              )}
              <div data-site-zoom className="flex rounded-md border border-border bg-background">
                <button type="button" aria-label={t('map.site.zoomIn')} title={t('map.site.zoomIn')} disabled={view.zoom >= SITE_MAX_ZOOM}
                  className={cn(zoomButton, 'rounded-l-md')} onClick={() => zoomBy(ZOOM_STEP)}>
                  <ZoomIn className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button type="button" aria-label={t('map.site.zoomOut')} title={t('map.site.zoomOut')} disabled={view.zoom <= 1}
                  className={cn(zoomButton, 'rounded-r-md border-l border-border')} onClick={() => zoomBy(1 / ZOOM_STEP)}>
                  <ZoomOut className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          ref={viewportRef}
          data-site-viewport
          data-zoom={view.zoom}
          className={cn(
            'relative min-h-0 min-w-0 flex-1 select-none overflow-hidden bg-muted',
            picked !== null ? 'cursor-crosshair' : view.zoom > 1 && 'cursor-grab',
          )}
          // #302 'auto' on a touch screen: one finger scrolls the page, two fingers are the drawing's (review M1)
          style={{ touchAction: 'pan-x pan-y' }}
          onPointerDown={onViewportPointerDown}
          onClick={onViewportClick}
        >
          {plan && !imageFailed && (
            <div
              data-site-stage
              className="absolute"
              style={{
                left: fit.left, top: fit.top, width: fit.width, height: fit.height,
                transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`, transformOrigin: '0 0',
              }}
            >
              <img
                src={plan.image_url}
                alt={t('map.site.drawingAlt')}
                draggable={false}
                className="pointer-events-none block h-full w-full select-none bg-white"
                onError={onImageError}
              />
              {drawn.map((c) => (
                <SitePin
                  key={c.id}
                  marker={markers.get(c.id)!}
                  at={positions.get(c.id)!}
                  zoom={view.zoom}
                  settings={settings}
                  selected={selectedId === c.id}
                  alertStart={alertStarts?.[c.id]}
                  arranging={arranging}
                  dragging={dragging === c.id}
                  onActivate={activate}
                  onDragStart={startDrag}
                  onThumbnailError={onThumbnailError}
                  thumbnailUrl={markers.get(c.id)!.thumbnailUrl}
                />
              ))}
            </div>
          )}

          {message && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground">
              <p className="m-0 flex items-center gap-2">{message}</p>
            </div>
          )}
        </div>
      </div>

      {arranging && (
        <aside
          aria-label={t('map.site.arrangeTitle')}
          className="flex min-h-0 shrink-0 flex-col border-border bg-background max-[760px]:max-h-[45%] max-[760px]:border-t min-[760px]:w-[280px] min-[760px]:border-l"
        >
          <header className="border-b border-border px-3 py-2">
            <h3 className="m-0 text-sm font-semibold">{t('map.site.arrangeTitle')}</h3>
            <p className="m-0 mt-0.5 text-xs text-muted-foreground">{t('map.site.arrangeHint')}</p>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {cameras.length === 0 && <p className="m-0 p-2 text-xs text-muted-foreground">{t('map.site.noCameras')}</p>}
            {cameras.map((c) => {
              const isPlaced = draft?.has(c.id) ?? false
              const isPicked = picked === c.id
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={isPicked}
                  onClick={() => setPicked((cur) => (cur === c.id ? null : c.id))}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                    isPicked ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
                  )}
                >
                  <span className={cn('min-w-[2rem] font-semibold tabular-nums', !isPicked && 'text-muted-foreground')}>{cameraNumber(c.name) ?? '–'}</span>
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  <span className={cn('shrink-0 text-[10px] font-semibold uppercase tracking-wide', !isPicked && (isPlaced ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'))}>
                    {isPlaced ? t('map.site.placed') : t('map.site.notPlacedState')}
                  </span>
                </button>
              )
            })}
          </div>
          <footer className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
            <Button size="sm" className="h-8" onClick={() => save.mutate(payload())} disabled={save.isPending}>
              {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('map.site.save')}
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={stopArranging} disabled={save.isPending}>
              {t('map.site.cancel')}
            </Button>
            <span className="text-xs text-muted-foreground">{t('map.site.placedCount', { placed, total: cameras.length })}</span>
          </footer>
        </aside>
      )}
    </div>
  )
}

/** One camera on the drawing. Positioned at its fractions inside the drawing (which zooms), and counter-scaled so it
 *  keeps its size; its tip is on the spot. Memoised: every prop is stable or a value. */
const SitePin = memo(function SitePin({
  marker, at: spot, zoom, settings, selected, alertStart, arranging, dragging, onActivate, onDragStart, onThumbnailError, thumbnailUrl,
}: {
  marker: MapMarker
  at: Point
  /** The drawing's zoom: the pin is drawn 1/zoom times, so it keeps its size; a zoom closes an open hover card (it
   *  would point at the old spot). A pan moves the drawing and its pins together and draws no pin again. */
  zoom: number
  settings: MapSettings
  selected: boolean
  alertStart?: number
  arranging: boolean
  dragging: boolean
  onActivate: (id: number) => void
  onDragStart: (id: number, e: ReactPointerEvent<HTMLElement>) => void
  onThumbnailError?: () => void
  thumbnailUrl?: string | null
}) {
  const { t } = useTranslation('cameras')
  const hover = useHoverIntent()
  const { close } = hover
  const [over, setOver] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  useEffect(() => { close() }, [zoom, arranging, close])

  const status = t(marker.isOnline === true ? 'map.pin.online' : marker.isOnline === false ? 'map.pin.offline' : 'map.pin.unknown')
  const active = (!arranging && (over || hover.open)) || dragging || selected
  const width = PIN_SIZE_WIDTH[settings.pin_size]
  const alert = marker.pulse ? alertGeometry(settings.pin_shape, width) : null
  const colour = PIN_COLOUR_HEX[settings.pin_colour]
  const end = () => { setOver(false); close() }

  return (
    <button
      type="button"
      data-site-pin={marker.id}
      aria-label={t('map.pin.ariaLabel', { name: marker.label, status })}
      className="group absolute h-0 w-0 border-0 bg-transparent p-0 outline-none"
      style={{
        left: asPercent(spot.x), top: asPercent(spot.y), transform: `scale(${1 / zoom})`, transformOrigin: '0 0',
        zIndex: active ? 3 : marker.pulse ? 2 : 1, cursor: arranging ? (dragging ? 'grabbing' : 'grab') : 'pointer',
        // arranging: a finger on a pin drags the pin, not the page
        touchAction: arranging ? 'none' : undefined,
      }}
      onMouseEnter={arranging ? undefined : () => { setOver(true); hover.enter() }}
      onMouseLeave={arranging ? undefined : () => { setOver(false); hover.leave() }}
      onClick={() => { end(); onActivate(marker.id) }}
      onPointerDown={arranging ? (e) => { end(); onDragStart(marker.id, e) } : undefined}
    >
      <div
        ref={anchorRef}
        className={cn(
          'detercam-pin rounded-full group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-1',
          active && 'detercam-pin--active', marker.pulse && 'detercam-pin--alert',
        )}
        style={{
          position: 'absolute', left: 0, bottom: 0, display: 'flex', transform: 'translateX(-50%)',
          ...(alert && ({ '--alert-bounce-1': `${alert.bounce[0]}px`, '--alert-bounce-2': `${alert.bounce[1]}px` } as CSSProperties)),
        }}
      >
        {/* #306 ripple — the Google map's alert, sized for this pin's shape and size */}
        {alert && (
          <span aria-hidden className="detercam-pin-pulse" style={{ top: alert.top, width: alert.box, height: alert.box }}>
            <span className="detercam-pin-glow" style={{ width: alert.glow, height: alert.glow }} />
            <span className="detercam-pin-halo" style={{ width: alert.ring, height: alert.ring, borderWidth: alert.ringBorder }} />
            <span className="detercam-pin-ripple" style={{ borderWidth: alert.rippleBorder, '--alert-ripple-from': alert.rippleFrom } as CSSProperties} />
          </span>
        )}
        <CameraPinSvg
          // #306 — a new alert draws the image anew, which bounces it again
          key={marker.pulse ? `alert-${alertStart ?? ''}` : 'pin'}
          isOnline={marker.isOnline}
          number={cameraNumber(marker.label)}
          shape={settings.pin_shape}
          colour={colour}
          width={width}
          offlineMark={settings.offline_mark}
          showNumber={settings.show_number}
        />
      </div>
      {hover.open && !arranging && (
        <PinHoverCard
          marker={marker}
          anchorRef={anchorRef}
          pinColour={colour}
          instant={hover.instant}
          onLost={close}
          onEnter={hover.enter}
          onLeave={hover.leave}
          onActivate={() => { end(); onActivate(marker.id) }}
          onThumbnailError={onThumbnailError}
          thumbnailUrl={thumbnailUrl}
        />
      )}
    </button>
  )
})
